// Copied verbatim from the desktop app so the two look identical.
// Do not edit here: change the app's copy, then run
//   node scripts/sync-app-components.mjs --write
// `pnpm verify:components` fails the build if the two drift apart.
import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * Caret sits in Radix’s arrow wrapper (`translateY(100%)` below the bubble for
 * side=top). A 1px pull toward the bubble covers the border so the tip’s base
 * meets the horizontal rule — no extra translate on a nested node (that left a gap).
 */
function TooltipCaret(): React.JSX.Element {
  return (
    <TooltipPrimitive.Arrow asChild>
      <svg
        width={14}
        height={7}
        viewBox="0 0 14 7"
        aria-hidden
        // -mt-px is on the arrow node itself (wrapper owns placement transform).
        className="z-50 -mt-px block fill-popover"
      >
        <path
          d="M0 0 L7 7 L14 0 Z"
          fill="var(--popover)"
          stroke="var(--border)"
          strokeWidth="1"
          strokeLinejoin="miter"
        />
        {/* Flat base = bubble border; hide the triangle’s top stroke. */}
        <path d="M0 0 L14 0" stroke="var(--popover)" strokeWidth="2" />
      </svg>
    </TooltipPrimitive.Arrow>
  );
}

function TooltipContent({
  className,
  sideOffset = 6,
  showCaret = false,
  container,
  portal = true,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  /** Tip that tracks the trigger — use for icon / info tooltips. */
  showCaret?: boolean;
  /** Portal host — use the fullscreen element so tips stay visible there. */
  container?: HTMLElement | null;
  /**
   * When false, render in-tree (no body portal). Use outside fullscreen so
   * page chrome stays above the tip.
   */
  portal?: boolean;
}) {
  const content = (
    <TooltipPrimitive.Content
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      // Keep the caret off rounded corners when the tip is near an edge.
      arrowPadding={10}
      className={cn(
        // `table` + max-w shrinks to the longest line instead of stretching to max-w
        // with a short last line (fit-content alone expands to the cap once wrapped).
        "pointer-events-auto group relative z-50 table max-w-56 origin-(--radix-tooltip-content-transform-origin) overflow-visible rounded-xl border border-border bg-popover px-3 py-1.5 text-left text-xs text-popover-foreground shadow-md",
        // Zoom scales the caret against the border and shows a clip/seam —
        // fade only when a diamond is shown.
        showCaret
          ? "animate-in fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          : "animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className
      )}
      {...props}
    >
      {children}
      {showCaret ? <TooltipCaret /> : null}
    </TooltipPrimitive.Content>
  );

  if (!portal) return content;

  return (
    <TooltipPrimitive.Portal container={container ?? undefined}>{content}</TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
